import * as moment from 'moment';
import { environment } from 'src/environments/environment';
import { SeguimientoAnonimoViajeRequest } from 'src/core/apis/seguimiento-anonimo.viajes';

export type QrClientFirmaPhase = 'waiting_open' | 'signing' | 'signed';

export const QR_CLIENT_POLL_MS = 15_000;
export const QR_CLIENT_WAIT_MS = 15 * 60 * 1000;

export interface QrClientFirmaInput {
  openedQrLink?: string;
  openedQrAt?: string;
  signedAt?: string;
  clientSignature?: string;
}

export interface QrClientFirmaStatus {
  phase: QrClientFirmaPhase;
  opened: boolean;
  signed: boolean;
  openedAtLabel: string;
  signedAtLabel: string;
}

const QR_TIME_FORMATS = [
  moment.ISO_8601,
  'YYYY-MM-DD HH:mm:ss',
  'YYYY-MM-DDTHH:mm:ss',
  'YYYY-MM-DD HH:mm',
  'DD/MM/YYYY HH:mm:ss',
  'DD/MM/YYYY HH:mm',
  'DD-MM-YYYY HH:mm:ss',
  'DD-MM-YYYY HH:mm',
  'DD-MMM-YY HH:mm:ss',
  'DD-MMM-YYYY HH:mm:ss',
  'DD-MMM-YY HH:mm',
  'DD-MMM-YY',
];

export const QR_WAIT_STORAGE_PREFIX = 'qr-client-wait-started:';

export function buildSeguimientoAnonimoParams(
  user: { KEY?: string; RUT_USUARIO?: string },
  tkt: string | number
): SeguimientoAnonimoViajeRequest {
  return {
    key: user.KEY ?? '',
    tkt_h: String(tkt),
    id1: btoa(String(user.RUT_USUARIO ?? '').replace('-', '')),
  };
}

export function buildSeguimientoQrLink(params: SeguimientoAnonimoViajeRequest): string {
  const path = environment.seguimientoAnonimoPublicPath ?? '/Seguimiento/3';
  const key = encodeURIComponent(params.key);
  const tkt_h = encodeURIComponent(params.tkt_h);
  const id1 = encodeURIComponent(params.id1);
  return `${window.location.origin}${path}?key=${key}&tkt_h=${tkt_h}&id1=${id1}`;
}

export function hasQrOpened(openedQrLink?: string): boolean {
  return String(openedQrLink ?? '').trim() === '1';
}

export function hasNonEmptyField(value?: string): boolean {
  const raw = String(value ?? '').trim();
  return raw !== '' && raw !== '0';
}

export function hasQrSigned(input: QrClientFirmaInput): boolean {
  return (
    hasQrOpened(input.openedQrLink) &&
    hasNonEmptyField(input.signedAt) &&
    hasNonEmptyField(input.clientSignature)
  );
}

export function deriveQrClientFirmaStatus(
  input: QrClientFirmaInput
): QrClientFirmaStatus {
  const opened = hasQrOpened(input.openedQrLink);
  const signed = hasQrSigned(input);
  const phase: QrClientFirmaPhase = signed
    ? 'signed'
    : opened
      ? 'signing'
      : 'waiting_open';
  return {
    phase,
    opened,
    signed,
    openedAtLabel: formatQrEventTime(input.openedQrAt),
    signedAtLabel: formatQrEventTime(input.signedAt),
  };
}

/** Un fallo de red conserva el último estado conocido. */
export function resolvePolledQrStatus(
  previous: QrClientFirmaStatus | null,
  next: QrClientFirmaStatus | null,
  failed: boolean
): QrClientFirmaStatus {
  if (failed || !next) {
    return previous ?? deriveQrClientFirmaStatus({});
  }
  return next;
}

export function remainingWaitMs(
  waitStartedAt: number,
  now: number,
  waitMs = QR_CLIENT_WAIT_MS
): number {
  return Math.max(0, waitMs - (now - waitStartedAt));
}

export function isWaitExpired(
  waitStartedAt: number | null,
  now: number,
  waitMs = QR_CLIENT_WAIT_MS
): boolean {
  if (waitStartedAt == null) return false;
  return remainingWaitMs(waitStartedAt, now, waitMs) === 0;
}

export function formatCountdown(remainingMs: number): string {
  const totalSec = Math.max(0, Math.ceil(remainingMs / 1000));
  const mm = Math.floor(totalSec / 60);
  const ss = totalSec % 60;
  return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
}

export function formatQrEventTime(raw?: string): string {
  const value = String(raw ?? '').trim();
  if (!value) return '';
  if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(value)) {
    return value.slice(0, 5);
  }
  const parsed = moment(value, QR_TIME_FORMATS, true);
  return parsed.isValid() ? parsed.format('HH:mm') : value;
}

export function parseQrEventTimestamp(raw?: string): number | null {
  const value = String(raw ?? '').trim();
  if (!value || /^\d{1,2}:\d{2}(:\d{2})?$/.test(value)) {
    return null;
  }
  const parsed = moment(value, QR_TIME_FORMATS, true);
  return parsed.isValid() ? parsed.valueOf() : null;
}

function waitStorageKey(tkt: string): string {
  return `${QR_WAIT_STORAGE_PREFIX}${tkt}`;
}

export function readQrWaitStartedAt(tkt: string): number | null {
  try {
    const raw = sessionStorage.getItem(waitStorageKey(tkt));
    const value = raw == null ? NaN : Number(raw);
    return Number.isFinite(value) ? value : null;
  } catch {
    return null;
  }
}

export function persistQrWaitStartedAt(tkt: string, startedAt: number): void {
  try {
    sessionStorage.setItem(waitStorageKey(tkt), String(startedAt));
  } catch {
    /* ignore quota / private mode */
  }
}

export function clearQrWaitStartedAt(tkt: string): void {
  try {
    sessionStorage.removeItem(waitStorageKey(tkt));
  } catch {
    /* ignore */
  }
}

/** Un solo stamp por viaje: API, sessionStorage o el primer now. */
export function resolveQrWaitStartedAt(input: {
  tkt: string;
  openedQrAt?: string;
  now?: number;
}): number {
  const fromApi = parseQrEventTimestamp(input.openedQrAt);
  if (fromApi != null) {
    persistQrWaitStartedAt(input.tkt, fromApi);
    return fromApi;
  }
  const stored = readQrWaitStartedAt(input.tkt);
  if (stored != null) {
    return stored;
  }
  const startedAt = input.now ?? Date.now();
  persistQrWaitStartedAt(input.tkt, startedAt);
  return startedAt;
}
